import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, Field, Spinner } from "./ui/kit";
import api, { apiError } from "../lib/api";
import Segment from "./Segment";
import Vyber from "./Vyber";
import { SeznamTymu } from "./TymKlienta";
import { useAuth } from "../context/AuthContext";
import { canSee } from "../lib/nav";
import { useAres, IcoAres, AresProuzek } from "./AresPole";

export default function ClientForm({ client = null, defaultPartnerId = null, onClose, onSaved }) {
  const editing = !!client;
  const [f, setF] = useState({
    typ: client?.typ || "OSOBNI",
    titul: client?.titul || "",
    jmeno: client?.jmeno || "",
    prijmeni: client?.prijmeni || "",
    nazev_firmy: client?.nazev_firmy || "",
    ico: client?.ico || "",
    dic: client?.dic || "",
    email: client?.email || "",
    telefon: client?.telefon || "",
    adresa: { ...(client?.adresa || { ulice: "", psc: "", mesto: "" }) },
    poznamky: client?.poznamky || "",
    servisni_smlouva: !!client?.servisni_smlouva,
    partner_id: client ? client.partner_id || "" : defaultPartnerId || "",
  });
  // Obchodní partner = kdo klienta dohodil. "" = bez partnera.
  const [partneri, setPartneri] = useState([]);
  useEffect(() => {
    api.get("/api/partneri").then((r) => setPartneri(r.data.items || [])).catch(() => {});
  }, []);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // Tým klienta — s kým je karta sdílená (kolegové, subdodavatelé). Přidaní dostanou e-mail.
  const { user } = useAuth();
  const jeMajitel = canSee(user, ["OWNER"]);
  const puvodniTym = (client?.tym || []).map((c) => c.id);
  const [tymIds, setTymIds] = useState(puvodniTym);
  const [lide, setLide] = useState([]);
  useEffect(() => {
    api.get("/api/team").then((r) => setLide((r.data.members || []).filter((m) => m.active))).catch(() => {});
  }, []);
  const tymZmenen = tymIds.length !== puvodniTym.length || tymIds.some((x) => !puvodniTym.includes(x));

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // IČO → ARES doplní název, DIČ a sídlo (Zrušit vrátí formulář, jak byl).
  const ares = useAres();
  const zAres = (a) => {
    ares.prevzit(a, { ...f, ico: a.ico });
    setF((x) => ({ ...x, nazev_firmy: a.nazev, ico: a.ico, dic: a.dic || x.dic, adresa: { ...x.adresa, ...a.adresa } }));
  };
  const sv = ares.firma ? ares.svit : "";
  const updA = (k) => (e) => setF({ ...f, adresa: { ...f.adresa, [k]: e.target.value } });

  const validate = () => {
    if (f.typ === "FIRMA" && !f.nazev_firmy.trim()) return "Zadej název firmy.";
    if (f.typ === "OSOBNI" && !f.prijmeni.trim()) return "Zadej příjmení.";
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return "Neplatný e-mail.";
    if (f.telefon && f.telefon.replace(/[\s+]/g, "").length < 9) return "Neplatné telefonní číslo.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setError(err);
    setError("");
    setSaving(true);
    try {
      // tym_ids jen při změně — jinak by uložení přepsalo tým, který mezitím upravil někdo jiný.
      const body = tymZmenen ? { ...f, tym_ids: tymIds } : f;
      if (editing) await api.patch(`/api/clients/${client.id}`, body);
      else await api.post("/api/clients", body);
      onSaved && onSaved();
      onClose();
    } catch (err2) {
      setError(apiError(err2.response?.data?.detail) || err2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="client-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[520px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="text-[16px] font-800 text-ink">{editing ? "Upravit klienta" : "Nový klient"}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink" data-testid="client-form-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Segment className="inline-flex p-1 bg-inputbg rounded-pill border border-line-strong">
            {[["OSOBNI", "Osobní"], ["FIRMA", "Firma"]].map(([v, l]) => (
              <button data-on={f.typ === v} type="button" key={v} onClick={() => setF({ ...f, typ: v })} data-testid={`typ-${v}`}
                className={`pill px-4 h-8 text-[12.5px] font-700 ${f.typ === v ? "text-white" : "text-sec"}`}>
                {l}
              </button>
            ))}
          </Segment>

          {f.typ === "FIRMA" && <AresProuzek firma={ares.firma} onZrusit={() => setF(ares.zrusit())} />}
          {f.typ === "FIRMA" && (
            <Field label="Název firmy">
              <input className={`field ${sv}`} value={f.nazev_firmy} onChange={upd("nazev_firmy")} data-testid="client-nazev-firmy" />
            </Field>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Titul" className="col-span-1">
              <input className="field" value={f.titul} onChange={upd("titul")} />
            </Field>
            <Field label="Jméno" className="col-span-1">
              <input className="field" value={f.jmeno} onChange={upd("jmeno")} data-testid="client-jmeno" />
            </Field>
            <Field label="Příjmení" className="col-span-1">
              <input className="field" value={f.prijmeni} onChange={upd("prijmeni")} data-testid="client-prijmeni" />
            </Field>
          </div>
          {f.typ === "FIRMA" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="IČO"><IcoAres value={f.ico} onChange={(v) => setF((x) => ({ ...x, ico: v }))} onNalezeno={zAres} className={sv} testid="client-ico" /></Field>
              <Field label="DIČ"><input className={`field amount ${sv}`} value={f.dic} onChange={upd("dic")} data-testid="client-dic" /></Field>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="E-mail"><input className="field" value={f.email} onChange={upd("email")} data-testid="client-email" /></Field>
            <Field label="Telefon"><input className="field amount" value={f.telefon} onChange={upd("telefon")} data-testid="client-telefon" /></Field>
          </div>
          <Field label="Ulice a č.p."><input className={`field ${sv}`} value={f.adresa.ulice} onChange={updA("ulice")} data-testid="client-ulice" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="PSČ"><input className={`field amount ${sv}`} value={f.adresa.psc} onChange={updA("psc")} data-testid="client-psc" /></Field>
            <Field label="Město"><input className={`field ${sv}`} value={f.adresa.mesto} onChange={updA("mesto")} data-testid="client-mesto" /></Field>
          </div>
          <Field label="Obchodní partner" hint="Firma, která klienta dohodila (architekt, stavební firma…).">
            <Vyber className="field" value={f.partner_id} onChange={upd("partner_id")} data-testid="client-partner">
              <option value="">bez partnera</option>
              {f.partner_id && !partneri.some((x) => x.id === f.partner_id) && (
                <option value={f.partner_id}>{client?.partner?.nazev || "…"}</option>
              )}
              {partneri.map((x) => <option key={x.id} value={x.id}>{x.nazev}</option>)}
            </Vyber>
          </Field>
          <Field label="Poznámky">
            <textarea className="field h-20 py-2 resize-none" value={f.poznamky} onChange={upd("poznamky")} />
          </Field>
          {/* Podle smlouvy poznáme, koho musíme na prohlídku obvolat sami. */}
          <label className="tap flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={f.servisni_smlouva} data-testid="client-smlouva"
              onChange={(e) => setF({ ...f, servisni_smlouva: e.target.checked })}
              className="h-4 w-4 accent-accent" />
            <span className="t-telo font-600 text-ink">Servisní smlouva</span>
          </label>

          {lide.length > 0 && (
            <div data-testid="client-tym">
              <span className="label-up text-muted block mb-1">Tým klienta</span>
              <p className="t-meta text-muted mb-1.5">S kým je klient sdílený. Subdodavatel uvidí jen tohoto klienta; přidaní dostanou e-mail.</p>
              <div className="border border-line rounded-xl p-1 max-h-[240px] overflow-y-auto">
                <SeznamTymu lide={lide} vybrane={tymIds} smiSub={jeMajitel} mujId={user?.id} testid="client-tym"
                  onPrepni={(uid) => setTymIds((v) => (v.includes(uid) ? v.filter((x) => x !== uid) : [...v, uid]))} />
              </div>
            </div>
          )}

          {error && <div className="text-[12.5px] text-danger">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-ghost">Zrušit</button>
            <Button disabled={saving} data-testid="client-save">
              {saving ? <Spinner /> : editing ? "Uložit" : "Založit klienta"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
